import React, { useState, useEffect, useContext, useRef } from 'react';
import './Thread.css';
import convenienceApi from '../../common/api/convenience';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addMinutes } from 'date-fns';
import MessageThread from '../MessageThread/MessageThread';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import baseApi from '../../common/api/base';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';

export default function Thread({ thread, lastReload, updateThreadLastActive, unreadThread, fullWidth }) {
  const environmentContext = useContext(EnvironmentContext);
  const [messages, setMessages] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(false);
  const [newMessageText, setNewMessageText] = useState('');
  const [needsScrollBottom, setNeedsScrollBottom] = useState(false);
  const messageRef = useRef(null);
  const setMessageRef = el => { messageRef.current = el; }
  const { activeUser: user, hasToken } = environmentContext;

  if (logComponent('Thread')) { console.debug('Thread rendered'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('Thread')) { console.debug('Thread onMount'); }
    if (!hasToken) return;

  }, [])

  useEffect(() => {
    if (logComponent('Thread')) { console.debug('Thread useEffect [thread.userMessageThreadId]'); }
    if (!hasToken) return;

    if (thread) loadThread();
  }, [thread, thread?.userMessageThreadId])

  useEffect(() => {
    if (logComponent('Thread')) { console.debug('Thread useEffect [lastReload]'); }
    if (!hasToken) return;

    if (thread) silentReloadThread();
  }, [lastReload])

  useEffect(() => {
    if (logComponent('Thread')) { console.debug('Thread useEffect [needsScrollBottom]'); }
    if (needsScrollBottom === true) {
      scrollMessagesToBottom();
      setNeedsScrollBottom(false);
}
  }, [needsScrollBottom])

  const loadThread = () => {
    if (logComponent('Thread')) { console.debug('Thread loadThread'); }
    if (!hasToken) return;

    setMessages(null);
    setMessagesLoading(true);
    setMessagesError(false);

    convenienceApi.getMessageThread(user, thread)
      .then(
        result => {
          setMessages(result);
          setMessagesLoading(false);
          setMessagesError(false);
          setNeedsScrollBottom(true);
        }, 
        error => {
          setMessages(null);
          setMessagesLoading(false);
          setMessagesError(true);
        }
      )
  }

  const silentReloadThread = () => {
    if (logComponent('Thread')) { console.debug('Thread silentReloadThread'); }
    if (!hasToken) return;

    convenienceApi.getMessageThread(user, thread)
      .then(
        result => {
          const needToScroll = !messages || messages.length !== result.length;
          setMessages(result);
          setMessagesLoading(false);
          setMessagesError(false);
          setNeedsScrollBottom(needToScroll);
        }, 
        error => { }
      )    
  }

  const updateTextarea = (e) => {
    if (logComponent('Thread')) { console.debug('Thread updateTextarea'); }
    setNewMessageText(e.target.value);
  }

  const scrollMessagesToBottom = () => {
    if (logComponent('Thread')) { console.debug('Thread scrollMessagesToBottom'); }

    if (!messageRef.current) return;

    const scrollDistance = messageRef.current.scrollHeight - messageRef.current.offsetHeight;

    messageRef.current.scrollTo(0, scrollDistance);
  }

  const sendMessage = () => {
    if (logComponent('Thread')) { console.debug('Thread sendMessage'); }
    if (!hasToken) return;

    const date = new Date();
    const newMessage = {
      message: newMessageText,
      createdByUserId: user.userId,
      userMessageThreadId: thread.userMessageThreadId
    };

    updateThreadLastActive();

    baseApi.UserMessage.Create(newMessage)
      .then(
        result => { },
        error => {
          // TODO: Mark any transmission error somehow in the UI
        }
      )

    // add temporary UserMessageId, to be used as a key until 
    // we get the actual key from the back-end with the next reload
    setTimeout(() => {
      newMessage.userMessageId = messages[messages.length - 1].userMessageId + 1;
      newMessage.sentOn = addMinutes(date, date.getTimezoneOffset());
      newMessage.createdByUser = user;

      setNewMessageText('');
      setMessages([...messages, newMessage]);
      setNeedsScrollBottom(true);
    });
  }

  const markAsUnread = () => {
    if (logComponent('Thread')) { console.debug('Thread markAsUnread'); }
    if (!hasToken) return;


    const isReadByFamily = user.groupTypeString === 'Family' ? false : thread.isReadByFamily;
    const isReadByFacility = user.groupTypeString === 'Facility' ? false : thread.isReadByFacility;

    thread.isReadByFamily = isReadByFamily;
    thread.isReadByFacility = isReadByFacility;

    unreadThread(thread);

    baseApi.UserMessage.ToggleReadStatus(thread.userMessageThreadId, isReadByFamily, isReadByFacility);
  }

  const handleMessageAreaKeyDown = (e) => {
    if (e.key === 'Enter') sendMessage();
  }

  return (
    <div className="Thread">
      {
        thread ?
          <React.Fragment>
            {
              messages &&
              (messages.length > 0 ?
                <React.Fragment>
                  {
                    fullWidth ?
                      <div className="Thread__info-bar Thread__info-bar--full-width">
                        <div className="Thread__info-row-1">
                          <p className="Thread__type">
                            {thread.isPrivate ? 'Direct message' : 'Family message'}
                            {user.groupTypeString === 'Facility' ? ` with ${thread.createdByUser.displayName}` : null}
                          </p>
                          <button
                            className="Thread__make-unread"
                            onClick={markAsUnread}>
                            <FontAwesomeIcon className="Thread__make-unread-icon" icon="envelope" /> Mark as unread
                          </button>
                        </div>
                        <p className="Thread__subject">{thread.Subject}</p>
                      </div> :
                      <div className="Thread__info-bar">
                        <div className="Thread__info">
                          <p className="Thread__type">
                            {thread.isPrivate ? 'Direct message' : 'Family message'}
                            {user.groupTypeString === 'Facility' ? ` with ${thread.createdByUser.displayName}` : null}
                          </p>
                          <p className="Thread__subject">{thread.Subject}</p>
                        </div>
                        <button
                          className="Thread__make-unread"
                          onClick={markAsUnread} >
                          <FontAwesomeIcon
                            className="Thread__make-unread-icon" icon="envelope" /> Mark as unread
                        </button>
                      </div>
                  }
                  <div
                    className="Thread__messages"
                    ref={setMessageRef}>
                    <MessageThread
                      thread={thread}
                      messages={messages} />
                    <div className="Thread__new-message-area">
                      <textarea
                        className="Thread__textarea"
                        onChange={updateTextarea}
                        onKeyDown={handleMessageAreaKeyDown}
                        value={newMessageText}
                        placeholder="Type your message here..."></textarea>
                      <button
                        className="Thread__send-message"
                        disabled={newMessageText.length <= 0}
                        onClick={sendMessage}>
                        Send message
                        <FontAwesomeIcon icon="arrow-right" className="Thread__send-message-icon" />
                      </button>
                    </div>
                  </div>
                </React.Fragment> :
                <p className="Thread__empty-message">No messages found...</p>)
            }
            <ModuleLoadingStatus isLoading={messagesLoading} isError={messagesError} reloadAction={loadThread} align="flex-start" />
          </React.Fragment> :
          <p className="Thread__empty-message">No conversation selected...</p>
      }
    </div>
  );
}
